import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Check, Clock3, LockKeyhole, Play, Radio, RefreshCw, ShieldAlert, Unlock, Users, WifiOff } from "lucide-react";
import { QrCode } from "./qr";

type SecurityEvent = { type: string; detail?: Record<string, unknown>; at: string };
type Participant = {
  userId: string;
  studentId?: string;
  fullName: string;
  joinedAt?: string | null;
  currentIndex: number;
  totalQuestions: number;
  status: "NOT_JOINED" | "ACTIVE" | "LOCKED" | "DONE";
  connection: "NOT_JOINED" | "ONLINE" | "OFFLINE" | "LOCKED" | "DONE";
  lastSeenAt?: string | null;
  lockedAt?: string | null;
  lockReason?: string | null;
  events: SecurityEvent[];
};

type SecureAdminState = {
  id: string;
  classId: string;
  className: string;
  mode: "prerequisite" | "evaluated";
  schoolLevel: string;
  theme: string;
  chapter: string;
  code: string;
  status: "lobby" | "question" | "finished";
  totalQuestions: number;
  participantCount: number;
  security: {
    shuffleQuestions: boolean;
    shuffleAnswers: boolean;
    lockOnVisibilityHidden: boolean;
    lockOnBlur: boolean;
    teacherUnlockRequired: boolean;
    durationMinutes: number;
  };
  remainingSeconds?: number | null;
  participants: Participant[];
  scores: Array<{ userId: string; fullName: string; correctCount: number; totalCount: number; score20: number; mastery: string }>;
  classAverage: { participantCount: number; score20: number | null; successPercent: number | null } | null;
};

async function api<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(path, { credentials: "include", headers: { "Content-Type": "application/json", ...(options?.headers ?? {}) }, ...options });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error ?? "Une erreur est survenue");
  return body as T;
}

function formatTime(value?: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleTimeString("fr-FR");
}

function formatTimer(seconds?: number | null) {
  if (seconds === null || seconds === undefined) return "";
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function eventLabel(event: SecurityEvent) {
  if (event.type === "hidden") return "Page / application masquée";
  if (event.type === "pagehide") return "Page quittée ou rechargée";
  if (event.type === "blur") return "Perte de focus";
  if (event.type === "locked") return "QCM verrouillé";
  if (event.type === "unlocked_by_teacher") return "Débloqué par le professeur";
  if (event.type === "auto_unlocked") return "Déblocage automatique";
  if (event.type === "online") return "Connexion rétablie";
  if (event.type === "offline") return "Connexion perdue";
  if (event.type === "time_expired") return "Temps écoulé";
  if (event.type === "answer") return `Réponse envoyée${typeof event.detail?.questionIndex === "number" ? ` · Q${event.detail.questionIndex}` : ""}`;
  return event.type;
}

export default function SecureQuizAdmin({ sessionId }: { sessionId: string }) {
  const [state, setState] = useState<SecureAdminState | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  async function refresh(silent = false) {
    try {
      const result = await api<{ session: SecureAdminState }>(`/api/secure-qcm/admin/sessions/${encodeURIComponent(sessionId)}`);
      setState(result.session);
      if (!silent) setError("");
    } catch (reason) { if (!silent) setError(reason instanceof Error ? reason.message : "Chargement impossible"); }
  }

  useEffect(() => { void refresh(); }, [sessionId]);
  useEffect(() => {
    if (!state || state.status === "finished") return;
    const timer = window.setInterval(() => void refresh(true), 850);
    return () => window.clearInterval(timer);
  }, [state?.id, state?.status]);

  async function control(action: "start" | "finish" | "unlock" | "unlock_all", studentUserId?: string) {
    if (!state || busy) return;
    if (action === "finish" && !window.confirm("Terminer le QCM pour toute la classe ?")) return;
    setBusy(studentUserId ? `${action}:${studentUserId}` : action); setError("");
    try {
      const result = await api<{ session: SecureAdminState }>(`/api/secure-qcm/admin/sessions/${state.id}/control`, { method: "POST", body: JSON.stringify({ action, studentUserId }) });
      setState(result.session);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Commande impossible"); }
    finally { setBusy(""); }
  }

  const lockedCount = useMemo(() => state?.participants.filter((item) => item.status === "LOCKED").length ?? 0, [state]);
  const offlineCount = useMemo(() => state?.participants.filter((item) => item.connection === "OFFLINE").length ?? 0, [state]);
  const doneCount = useMemo(() => state?.participants.filter((item) => item.status === "DONE").length ?? 0, [state]);
  // Garder l'ordre initial à l'intérieur de chaque groupe : les élèves terminés
  // passent à la fin à chaque actualisation, sans masquer les élèves en difficulté.
  const participantsInDisplayOrder = useMemo(() => {
    const participants = state?.participants ?? [];
    return [
      ...participants.filter((item) => item.status !== "DONE"),
      ...participants.filter((item) => item.status === "DONE"),
    ];
  }, [state]);

  if (!state) return <main className="secure-qcm-admin-shell"><div className="secure-qcm-admin-loading"><RefreshCw className="spin" /><h1>Chargement du QCM sécurisé…</h1>{error && <p>{error}</p>}</div></main>;

  const joinUrl = `${window.location.origin}/qcm/${state.code}`;

  return <main className="secure-qcm-admin-shell">
    <header className="secure-qcm-admin-topbar"><div><a href="/qcm-admin"><ArrowLeft size={18} /> QCM</a><span>MODE INDIVIDUEL SÉCURISÉ</span><h1>{state.className}</h1><p>{state.theme ? `${state.theme} · ` : ""}{state.chapter}</p></div><div className="secure-qcm-admin-kpis"><div><Users size={18} /><strong>{state.participantCount}</strong><span>connectés</span></div><div className={lockedCount ? "danger" : ""}><LockKeyhole size={18} /><strong>{lockedCount}</strong><span>bloqués</span></div><div className={offlineCount ? "warning" : ""}><WifiOff size={18} /><strong>{offlineCount}</strong><span>hors ligne</span></div><div><Check size={18} /><strong>{doneCount}</strong><span>terminés</span></div></div></header>
    {error && <div className="secure-qcm-admin-error">{error}</div>}

    {state.status === "lobby" && <section className="secure-qcm-admin-lobby"><div><span>Code de connexion</span><strong>{state.code}</strong><p>{joinUrl}</p><div className="secure-qcm-security-summary"><b>Questions mélangées : {state.security.shuffleQuestions ? "oui" : "non"}</b><b>Réponses mélangées : {state.security.shuffleAnswers ? "oui" : "non"}</b><b>Sortie d’activité : verrouillage {state.security.lockOnVisibilityHidden ? "actif" : "inactif"}</b><b>Perte de focus : {state.security.lockOnBlur ? "verrouillage" : "journalisation seulement"}</b>{state.security.durationMinutes > 0 && <b>Chronomètre : {state.security.durationMinutes} min</b>}</div><button className="secure-qcm-admin-primary" onClick={() => void control("start")} disabled={Boolean(busy)}><Play size={20} /> Lancer le QCM sécurisé</button></div><div className="secure-qcm-admin-qr"><QrCode value={joinUrl} size={300} title="QR code pour rejoindre le QCM sécurisé" /><span>Scanner pour rejoindre</span></div></section>}

    {state.status === "question" && <>
      <section className="secure-qcm-admin-actions"><div><Radio size={20} /><strong>QCM en cours</strong><span>Chaque élève avance à son rythme avec un ordre différent.</span>{state.remainingSeconds !== null && state.remainingSeconds !== undefined && <b className="secure-qcm-admin-timer"><Clock3 size={18} /> {formatTimer(state.remainingSeconds)}</b>}</div><div>{lockedCount > 0 && <button className="secure-qcm-admin-unlock-all" disabled={Boolean(busy)} onClick={() => void control("unlock_all")}><Unlock size={17} /> Débloquer tous ({lockedCount})</button>}<button className="secure-qcm-admin-finish" disabled={Boolean(busy)} onClick={() => void control("finish")}>Terminer le QCM</button></div></section>
      <section className="secure-qcm-participant-grid">{participantsInDisplayOrder.map((participant) => <article className={`secure-qcm-participant ${participant.status === "DONE" ? "completed" : participant.connection.toLowerCase()}`} key={participant.userId}>
        <div className="secure-qcm-participant-head"><div><strong>{participant.fullName}</strong><span>{participant.status === "NOT_JOINED" ? "Non connecté" : participant.status === "LOCKED" ? "QCM BLOQUÉ" : participant.status === "DONE" ? "Terminé" : participant.connection === "OFFLINE" ? "Connexion perdue" : "En cours"}</span></div>{participant.status === "LOCKED" ? <LockKeyhole size={23} /> : participant.status === "DONE" ? <Check size={22} /> : participant.connection === "OFFLINE" ? <WifiOff size={22} /> : <Radio size={20} />}</div>
        <div className="secure-qcm-participant-progress"><span>Question {Math.min(participant.currentIndex + (participant.status === "DONE" ? 0 : 1), participant.totalQuestions)}/{participant.totalQuestions}</span><div><i style={{ width: `${Math.round((participant.currentIndex / Math.max(1, participant.totalQuestions)) * 100)}%` }} /></div></div>
        {participant.status === "LOCKED" && <div className="secure-qcm-lock-detail"><span>Sortie détectée à {formatTime(participant.lockedAt)}</span><small>{participant.lockReason === "blur" ? "Perte de focus" : participant.lockReason === "pagehide" ? "Page quittée / rechargée" : "Application ou onglet masqué"}</small><button disabled={Boolean(busy)} onClick={() => void control("unlock", participant.userId)}><Unlock size={15} /> Débloquer</button></div>}
        {participant.status === "ACTIVE" && participant.connection === "OFFLINE" && <div className="secure-qcm-offline-note">Pas de signal récent du navigateur. Aucune sortie n’est accusée automatiquement.</div>}
        {participant.events.length > 0 && <details className="secure-qcm-events"><summary>Historique ({participant.events.length})</summary><div>{participant.events.map((event, index) => <p key={`${event.at}-${index}`}><time>{formatTime(event.at)}</time><span>{eventLabel(event)}</span></p>)}</div></details>}
      </article>)}</section>
    </>}

    {state.status === "finished" && <section className="secure-qcm-admin-finished"><Check size={52} /><h2>QCM terminé</h2><p>{state.mode === "evaluated" ? "Les notes ont été calculées et enregistrées." : "QCM diagnostic terminé, sans note."}</p>
      <div className="secure-qcm-class-average" aria-label="Moyenne du QCM pour la classe">
        <span>{state.mode === "evaluated" ? "Moyenne de la classe" : "Réussite moyenne de la classe"}</span>
        <strong>{state.classAverage && state.classAverage.participantCount > 0
          ? state.mode === "evaluated"
            ? `${state.classAverage.score20?.toFixed(1).replace(".0", "")} / 20`
            : `${state.classAverage.successPercent?.toFixed(1).replace(".0", "")} %`
          : "—"}</strong>
        <small>{state.classAverage && state.classAverage.participantCount > 0
          ? `Calculée sur ${state.classAverage.participantCount} élève${state.classAverage.participantCount > 1 ? "s" : ""} ayant répondu à au moins une question ; les questions non traitées comptent pour zéro.`
          : "Aucune réponse enregistrée."}</small>
      </div>
      {state.mode === "evaluated" && <div className="secure-qcm-score-list">{state.scores.map((score) => <div key={score.userId}><span>{score.fullName}</span><small>{score.correctCount}/{score.totalCount}</small><strong>{score.score20.toFixed(1).replace(".0", "")} / 20</strong><b>{score.mastery}</b></div>)}</div>}<a className="secure-qcm-admin-primary" href="/qcm-admin">Retour aux QCM</a></section>}
  </main>;
}
